import React, { useEffect, useState } from "react"
import styled from "styled-components"
import GaugeChart from "./Charts/GaugeChart"
import { lastMonthFormatted2 } from "../util/getDate"
import axios from "axios"
import { CO2_FACTOR } from "../util/Factor"

const Card = styled.div`
  display: flex;
  width: 416px;
  height: 208px;
  padding: 16px;
  justify-content: center;
  align-items: center;

  border-radius: 16px;
  border: 1px solid var(--base-gray40, #bfbfbf);
  background: var(--base-white100, #fff);
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
`

const Header = styled.div`
  align-self: stretch;
  color: var(--base-gray90, #404040);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
  align-self: stretch;
`

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`

const ChartWrapper = styled.div`
  display: flex;
  width: 168px;
  height: 84px;
  /* padding-bottom: 8px; */
  justify-content: center;
  align-items: flex-end;
`

const ChartSubWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`

const ChartSubItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const ChartSubItemHeader1 = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: var(--orange10, #fceddd);

  color: var(--orange100, #e06c00);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const ChartSubItemHeader2 = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: var(--orange10, #fceddd);
  border-radius: 4px;
  background: var(--base-gray10, #f2f2f2);

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const ChartSubItemSub = styled.div`
  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const RightContent = styled.div`
  display: flex;
  width: 92px;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

const RightTitle = styled.div`
  align-self: stretch;
  color: var(--base-gray100, #262626);
  text-align: center;
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const RightLine = styled.div`
  width: 80px;
  height: 1px;
  background: var(--orange100, #e06c00);
`

const RightMain = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

const RightMainTitle = styled.div`
  align-self: stretch;
  color: var(--base-gray80, #5e5e5e);
  text-align: center;
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const RightMainContent = styled.div`
  align-self: stretch;
  color: var(--orange100, #e06c00);
  text-align: center;
  font-family: SUIT;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

interface DashBoardMonthData {
  previousMonthGeneration: number // 저번 달 생산량
  previousMonthConsumption: number // 저번 달 소비량
  previousMonthEmissions: number // 저번 달 온실가스 배출량
  previousReductions: number // 저번 달 온실가스 감축량
}

interface Percentage {
  selfSufficiencyRate?: number // 자립률 (Self-Sufficiency Rate)
  reductionRate?: number // 감축률 (Reduction Rate)
  comparativeSelfSufficiencyRate?: number // 비교 자립률 (Comparative Self-Sufficiency Rate)
  comparativeReductionRate?: number // 비교 감축률
}

const Card3 = () => {
  const [data, setData] = useState<DashBoardMonthData>({
    previousMonthGeneration: 0,
    previousMonthConsumption: 0,
    previousMonthEmissions: 0,
    previousReductions: 0,
  })

  const [percentage, setPercentage] = useState<Percentage>({
    selfSufficiencyRate: 0,
    reductionRate: 0,
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/smart-farm/dashboard-month`
        )
        const {
          previousMonthGeneration,
          previousMonthConsumption,
          lastYearPreviousMonthGeneration,
          lastYearPreviousMonthConsumption,
        } = response.data

        const previousMonthEmissions = previousMonthConsumption * CO2_FACTOR
        const previousReductions = previousMonthGeneration * CO2_FACTOR

        const lastYearPreviousEmissions =
          lastYearPreviousMonthConsumption * CO2_FACTOR
        const lastYearPreviousReductions =
          lastYearPreviousMonthGeneration * CO2_FACTOR

        setData({
          previousMonthGeneration,
          previousMonthConsumption,
          previousMonthEmissions,
          previousReductions,
        })

        setPercentage({
          selfSufficiencyRate:
            (previousMonthGeneration / previousMonthConsumption) * 100,
          reductionRate: (previousReductions / previousMonthEmissions) * 100,
          comparativeSelfSufficiencyRate:
            (previousMonthGeneration / previousMonthConsumption -
              lastYearPreviousMonthGeneration /
                lastYearPreviousMonthConsumption) *
            100,
          comparativeReductionRate:
            (previousReductions / previousMonthEmissions -
              lastYearPreviousReductions / lastYearPreviousEmissions) *
            100,
        })
      } catch (error) {
        console.error("Error fetching dashboard data:", error)
      }
    }

    fetchData()
  }, [])

  return (
    <Card>
      <Wrapper>
        <Header>에너지 자립률</Header>
        <Content>
          <LeftContent>
            <ChartWrapper>
              <GaugeChart
                value={Number(percentage.selfSufficiencyRate) || 0}
                total={100}
                color={"#FAA93E"}
              />
            </ChartWrapper>
            <ChartSubWrapper>
              <ChartSubItem>
                <ChartSubItemHeader1>재생 E 발전량</ChartSubItemHeader1>
                <ChartSubItemSub>
                  {Number(
                    (data.previousMonthGeneration / 1000).toFixed(0)
                  ).toLocaleString()}{" "}
                  MWh
                </ChartSubItemSub>
              </ChartSubItem>
              <ChartSubItem>
                <ChartSubItemHeader2>에너지 소비량</ChartSubItemHeader2>
                <ChartSubItemSub>
                  {Number(
                    (data.previousMonthConsumption / 1000).toFixed(0)
                  ).toLocaleString()}{" "}
                  MWh
                </ChartSubItemSub>
              </ChartSubItem>
            </ChartSubWrapper>
          </LeftContent>
          <RightContent>
            <RightTitle>{lastMonthFormatted2}</RightTitle>
            <RightLine />
            <RightMain>
              <RightMainTitle>전년 동월 대비</RightMainTitle>
              <RightMainContent>
                {percentage.comparativeSelfSufficiencyRate !== undefined &&
                percentage.comparativeSelfSufficiencyRate !== null ? (
                  <>
                    {Math.abs(
                      percentage.comparativeSelfSufficiencyRate
                    ).toFixed(1)}
                    %{/* 증감 표시 */}
                    {percentage.comparativeSelfSufficiencyRate >= 0
                      ? " 증가"
                      : " 감소"}
                  </>
                ) : (
                  "0%"
                )}
              </RightMainContent>
            </RightMain>
          </RightContent>
        </Content>
      </Wrapper>
    </Card>
  )
}

export default Card3
